import { useState, useEffect } from 'react';
import { Phone, Loader2, CheckCircle, XCircle, X } from 'lucide-react';
import { apiRequest } from '@/utils/api-request';
import Button from '../common/Button';

interface InitiateCallModalProps {
  isOpen: boolean;
  onClose: () => void;
  leadUuid: string;
  leadName: string;
  leadPhone: string;
}

type CallState = 'initiating' | 'success' | 'error';

export default function InitiateCallModal({
  isOpen,
  onClose,
  leadUuid,
  leadName,
  leadPhone
}: InitiateCallModalProps) {
  const [callState, setCallState] = useState<CallState>('initiating');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [callData, setCallData] = useState<any>(null);

  useEffect(() => {
    if (isOpen) {
      initiateCall();
    }
  }, [isOpen]);

  const initiateCall = async () => {
    try {
      setCallState('initiating');
      setErrorMessage('');

      const response = await apiRequest('/api/outbound-call/call', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          lead_uuid: leadUuid
        })
      });

      if (response.success) {
        setCallState('success');
        setCallData(response.data);

        // Auto-close after 3 seconds on success
        setTimeout(() => {
          onClose();
        }, 3000);
      } else {
        setCallState('error');
        setErrorMessage(response.message || 'Failed to initiate call');
      }
    } catch (error: any) {
      setCallState('error');
      setErrorMessage(error.message || 'Failed to initiate call');
    }
  };

  const handleClose = () => {
    setCallState('initiating');
    setErrorMessage('');
    setCallData(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[80] flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Initiating Call</h3>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Lead Information */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <Phone className="w-5 h-5 text-brandColor" />
              </div>
              <div>
                <p className="font-medium text-gray-900">{leadName}</p>
                <p className="text-sm text-gray-600">{leadPhone}</p>
              </div>
            </div>
          </div>

          {/* Call State */}
          <div className="mb-6">
            {callState === 'initiating' && (
              <div className="text-center py-8">
                <Loader2 className="w-12 h-12 text-brandColor animate-spin mx-auto mb-4" />
                <p className="text-gray-900 font-medium mb-1">Connecting call...</p>
                <p className="text-sm text-gray-600">Please wait while we connect your call</p>
              </div>
            )}

            {callState === 'success' && (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                <p className="text-gray-900 font-medium mb-1">Call initiated successfully!</p>
                <p className="text-sm text-gray-600">You should receive a call shortly</p>
                {callData?.call_uuid && (
                  <p className="text-xs text-gray-500 mt-2">Call ID: {callData.call_uuid}</p>
                )}
                <p className="text-xs text-gray-500 mt-3">Closing in 3 seconds...</p>
              </div>
            )}

            {callState === 'error' && (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                  <XCircle className="w-10 h-10 text-red-600" />
                </div>
                <p className="text-gray-900 font-medium mb-1">Call failed</p>
                <p className="text-sm text-red-600">{errorMessage}</p>
              </div>
            )}
          </div>

          {/* Actions */}
          {callState === 'error' && (
            <div className="flex items-center justify-end gap-3">
              <Button variant="outline" onClick={handleClose}>
                Close
              </Button>
              <Button onClick={initiateCall}>
                Try Again
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
